"use client"

import { useEffect, useState, useCallback, useContext } from "react"
import axios from "axios"
import { useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { Eye, Printer, X, FileText, AlertCircle, CheckCircle, RefreshCw, ShoppingBag, RotateCcw, ShieldCheck } from 'lucide-react'
import jsPDF from "jspdf"
import "jspdf-autotable"
import "../styles/meusPedidos.css"
import { UserContext } from "../context/UserContext"
import logo from "../assets/2.png"
import JsBarcode from 'jsbarcode';
import "./meusPedidosReembolso.css"

const MeusPedidos = () => {
  const [pedidos, setPedidos] = useState([])
  const [loading, setLoading] = useState(true)
  const [pedidoSelecionado, setPedidoSelecionado] = useState(null)
  const [confirmacaoReembolso, setConfirmacaoReembolso] = useState(false)
  const [enviandoReembolso, setEnviandoReembolso] = useState(false)
  const [erroReembolso, setErroReembolso] = useState("")
  const navigate = useNavigate()
  const { user, token, isLoading } = useContext(UserContext)

  const irParaPagamento = (pedido) => {
  navigate("/pagamento", {
    state: {
      pedidoId: pedido._id,
      nome: pedido.nomeVaqueiro,
      categoriaSelecionada: pedido.categoria,
      totalPedido: pedido.total,
      nomeEvento: pedido.evento?.nome,
      eventoid: pedido.evento?._id,
      senhasEscolhidas: {}, // não precisa aqui, pois o pedido já existe
    },
  })
}

  const carregarPedidos = useCallback(async () => {
    if (!user || !user._id) {
      navigate("/login")
      return
    }

    try {
      setLoading(true)
      const response = await axios.get(`https://api.ticketdovaqueiro.com/api/pedidos/usuario/${user._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.data && Array.isArray(response.data)) {
        setPedidos(response.data)
      } else {
        setPedidos([])
      }
    } catch (error) {
      console.error("Erro ao buscar pedidos:", error)
    } finally {
      setLoading(false)
    }
  }, [user, token, navigate])

  useEffect(() => {
    if (!isLoading) {
      carregarPedidos()
    }
  }, [isLoading, carregarPedidos])

  const abrirModal = (pedido) => {
    setPedidoSelecionado(pedido)
  }

  const fecharModal = () => {
    setPedidoSelecionado(null)
    setConfirmacaoReembolso(false)
    setErroReembolso("")
  }

  const enviarSolicitacaoReembolso = async () => {
    if (!pedidoSelecionado?._id || enviandoReembolso) return
    setEnviandoReembolso(true)
    setErroReembolso("")
    try {
      const response = await axios.post(
        `https://api.ticketdovaqueiro.com/api/pedidos/${pedidoSelecionado._id}/solicitar-reembolso`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      const atualizado = {
        ...pedidoSelecionado,
        solicitacaoReembolso: {
          ...(pedidoSelecionado.solicitacaoReembolso || {}),
          status: response.data?.status,
          solicitadoEm: new Date().toISOString(),
        },
      }
      setPedidoSelecionado(atualizado)
      setPedidos((atuais) => atuais.map((item) => item._id === atualizado._id ? atualizado : item))
      setConfirmacaoReembolso(false)
    } catch (error) {
      if (error.response?.data?.status === "erro_gateway") {
        const atualizado = {
          ...pedidoSelecionado,
          solicitacaoReembolso: { status: "erro_gateway", solicitadoEm: new Date().toISOString() },
        }
        setPedidoSelecionado(atualizado)
        setPedidos((atuais) => atuais.map((item) => item._id === atualizado._id ? atualizado : item))
        setConfirmacaoReembolso(false)
      }
      setErroReembolso(error.response?.data?.mensagem || "Não foi possível enviar a solicitação. Tente novamente.")
    } finally {
      setEnviandoReembolso(false)
    }
  }

  const nomeStatusReembolso = (status) => ({
    em_processamento: "Estorno solicitado à Asaas",
    enviando_suporte: "Enviando solicitação ao suporte",
    solicitado_suporte: "Aguardando análise do suporte",
    falha_notificacao: "Falha ao avisar o suporte — tente novamente",
    erro_gateway: "Solicitação precisa de análise da equipe",
    concluido: "Reembolso concluído",
    negado: "Solicitação não aprovada",
  }[status] || "Solicitação em andamento")

  const pagamentoEhPixAsaas = (pedido) =>
    String(pedido?.metodoPagamento || "").toLowerCase() === "pix" &&
    String(pedido?.detalhesPagamento?.gateway || "").toLowerCase() === "asaas"

  const gerarPDF = (pedido) => {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const nomeEvento = pedido.evento?.nome?.toUpperCase()?.replace(" DO ", "\nDO ");
    const codigoAutenticacao = pedido._id.slice(-6).toUpperCase() + "-" + Math.floor(100000 + Math.random() * 900000);
    // Centraliza e desenha o título de autenticidade e o código
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text("Autenticidade do Pedido", pageWidth / 2, 272, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Código: ${codigoAutenticacao}`, pageWidth / 2, 278, { align: "center" });

    // Gera o código de barras em canvas
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, codigoAutenticacao, {
      format: "CODE128",
      width: 1.5,
      height: 10, // altura mais discreta
      displayValue: false,
      margin: 0,
    });
    const barcodeImage = canvas.toDataURL("image/png");

    // Insere o código de barras centralizado abaixo dos textos
    const barcodeWidth = 80;
    const barcodeX = (pageWidth - barcodeWidth) / 2;
    doc.addImage(barcodeImage, "PNG", barcodeX, 280, barcodeWidth, 15);

    // Logo
    const img = new Image();
    img.src = logo;
    img.crossOrigin = "anonymous";
  
    img.onload = () => {
      doc.addImage(img, "PNG", 75, 5, 45, 20);
  
      // Título principal
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text(nomeEvento, pageWidth / 2, 30, { align: "center" });
  
      // Subtítulo
  
  
      doc.setDrawColor("#FFD700");
      doc.setFillColor("#FFD700");
      doc.rect(10, 43, pageWidth - 20, 10, "F");
  
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text("DADOS DO VAQUEIRO", pageWidth / 2, 50, { align: "center" });

      // Dados do Vaqueiro
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text("NOME DO VAQUEIRO:", 15, 60);
      doc.setFont("helvetica", "normal");
      doc.text(pedido.nomeVaqueiro || "Não informado", 58, 60);
  
      doc.setFont("helvetica", "bold");
      doc.text("CPF:", 155, 60);
      doc.setFont("helvetica", "normal");
      doc.text(pedido.cpf || "Não informado", 165, 60);
  
      doc.setFont("helvetica", "bold");
      doc.text("NOME DO ESTEIRA:", 15, 68);
      doc.setFont("helvetica", "normal");
      doc.text(pedido.nomeEsteira || "Não informado", 55, 68);
  
      doc.setFont("helvetica", "bold");
      doc.text("CAVALO DE PUXADA:", 15, 76);
      doc.setFont("helvetica", "normal");
      doc.text(pedido.cavaloPuxar || "Não informado", 58, 76);
  
      doc.setFont("helvetica", "bold");
      doc.text("CAVALO DE ESTEIRA:", 15, 84);
      doc.setFont("helvetica", "normal");
      doc.text(pedido.cavaloEsteira || "Não informado", 58, 84);
  
      doc.setFont("helvetica", "bold");
      doc.text("REPRESENTAÇÃO:", 15, 92);
      doc.setFont("helvetica", "normal");
      doc.text(pedido.representacao || "Não informado", 53, 92);
  
      doc.setFont("helvetica", "bold");
      doc.text("CIDADE:", 15, 100);
      doc.setFont("helvetica", "normal");
      doc.text(pedido.cidade || "Não informado", 33, 100);
  
      // Infos do pedido
      doc.setDrawColor("#FFD700");
      doc.setFillColor("#FFD700");
      doc.rect(10, 108, pageWidth - 20, 10, "F");
  
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text("INFORMAÇÕES DO PEDIDO", pageWidth / 2, 116, { align: "center" });
  
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("CATEGORIA:", 15, 125);
      doc.setFont("helvetica", "normal");
      doc.text(pedido.categoria || "-", 40, 125);
  
      doc.setFont("helvetica", "bold");
      doc.text("FORMA DE PAGAMENTO:", 15, 132);
      doc.setFont("helvetica", "normal");
      doc.text(pedido.metodoPagamento || "-", 65, 132);
  
      doc.setFont("helvetica", "bold");
      doc.text("DATA DA COMPRA:", 15, 139);
      const dataCompra = new Date(pedido.dataCriacao).toLocaleString("pt-BR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit"
      }).replace(",", " às").replace(":", "H");
      doc.setFont("helvetica", "normal");
      doc.text(dataCompra, 53, 139);
  
      doc.setFont("helvetica", "bold");
      doc.text("STATUS DO PAGAMENTO:", 118, 125);
      doc.setFont("helvetica", "normal");
      doc.text(pedido.statusPagamento?.toUpperCase() || "-", 170, 125);
  
      doc.setFont("helvetica", "bold");
      doc.text("TOTAL:", 125, 132);
      doc.setFont("helvetica", "normal");
      doc.text(`R$ ${Number(pedido.total).toFixed(2).replace(".", ",")}`, 140, 132);
  
      // Senhas Adquiridas
      doc.setDrawColor("#FFD700");
      doc.setFillColor("#FFD700");
      doc.rect(10, 147, pageWidth - 20, 10, "F");
  
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text("SENHAS ADQUIRIDAS", pageWidth / 2, 155, { align: "center" });
  
      const colunasPorLinha = 4;
      const larguraColuna = 45;
      const altura = 28;
      const espacamento = 5;
      let startY = 165;
  
      pedido.senhas.forEach((senha, i) => {
        const coluna = i % colunasPorLinha;
        const linha = Math.floor(i / colunasPorLinha);
  
        const x = 15 + coluna * (larguraColuna + espacamento);
        const y = startY + linha * (altura + 10);
  
        doc.setFillColor(255, 236, 93);
        doc.roundedRect(x, y, larguraColuna, altura, 3, 3, "F");
  
        doc.setTextColor(0, 0, 0);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text(`SENHA ${i + 1}`, x + 14, y + 6);
        // Caixa amarela com borda arredondada
        doc.setFillColor(255, 236, 93); // cor de fundo
        doc.roundedRect(x, y, larguraColuna, altura, 3, 3, "F");

        const centerX = x + (larguraColuna / 2);

        doc.setTextColor(0, 0, 0);

        // Título: SENHA N
        doc.setFont("helvetica", "bold").setFontSize(10);
        const titulo = `SENHA ${i + 1}`;
        doc.text(titulo, centerX - doc.getTextWidth(titulo) / 2, y + 6);

        // Número centralizado
        doc.setFontSize(30);
        const numeroTexto = `${senha.numero}`;
        doc.text(numeroTexto, centerX - doc.getTextWidth(numeroTexto) / 2, y + 15);

        // BOI TV
        doc.setFont("helvetica", "normal").setFontSize(10);
        const boiTvTexto = `BOI TV: ${senha.boiTv ? "SIM" : "NÃO"}`;
        doc.text(boiTvTexto, centerX - doc.getTextWidth(boiTvTexto) / 2, y + 22);

        // Taxas opcionais
        if (senha.taxasOpcionaisSelecionadas?.length > 0) {
          doc.setFontSize(8);
          senha.taxasOpcionaisSelecionadas.forEach((taxa, i) => {
            const taxaTexto = `${taxa.nome.toUpperCase()}: R$ ${taxa.valor.toFixed(2).replace(".", ",")}`;
            doc.text(taxaTexto, centerX - doc.getTextWidth(taxaTexto) / 2, y + 26 + (i * 4));
          });
        }




        
      });
  
      doc.save(`Pedido_${pedido._id}.pdf`);
    };
  };
  

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "pago":
        return "hg-status-pago"
      case "pendente":
        return "hg-status-pendente"
      case "cancelado":
        return "hg-status-cancelado"
      case "reservado":
        return "hg-status-reservado"
      case "reembolsado":
        return "hg-status-reembolsado"  
      default:
        return ""
    }
  }

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "pago":
        return <CheckCircle size={13} className="hg-status-icon" />
      case "pendente":
        return <RefreshCw size={13} className="hg-status-icon" />
      case "cancelado":
        return <X size={13} className="hg-status-icon" />
      case "reservado":
        return <AlertCircle size={13} className="hg-status-icon" />
      case "reembolsado":
        return <RefreshCw size={12} className="hg-status-icon" />  
      default:
        return null
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="hg-pedidos-container"
    >
      <motion.h1
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <ShoppingBag className="hg-title-icon" />
        Meus Pedidos
      </motion.h1>

      {loading ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="hg-loading-container"
        >
          <RefreshCw className="hg-loading-icon" />
          <p>Carregando seus pedidos...</p>
        </motion.div>
      ) : pedidos.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="hg-empty-container"
        >
          <AlertCircle size={48} className="hg-empty-icon" />
          <h3>Nenhum pedido encontrado</h3>
          <p>Você ainda não realizou nenhuma compra em nossa plataforma.</p>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="hg-table-container"
        >
          <table className="hg-pedidos-tabela">
            <thead>
              <tr>
                <th>Evento</th>
                <th>Categoria</th>
                <th>Senhas</th>
                <th>Valor Total</th>
                <th>Status</th>
                <th>Data</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {pedidos.map((pedido) => (
                <motion.tr
                  key={pedido._id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <td>{pedido.evento?.nome || "Evento não encontrado"}</td>
                  <td>{pedido.categoria}</td>
                  <td className="hg-senhas-cell">
                    {pedido.senhas.map((s, index) => (
                      <span key={s.numero} className={`hg-senha-badge ${getStatusClass(s.status)}`}>
                        {s.numero}
                        {index !== pedido.senhas.length - 1 ? " | " : ""}
                      </span>
                    ))}
                  </td>
                  <td>R$ {pedido.total.toFixed(2).replace(".", ",")}</td>
                  <td>
  <div className={getStatusClass(pedido.statusPagamento)}>
    {getStatusIcon(pedido.statusPagamento)}
    <span>{pedido.statusPagamento?.toUpperCase()}</span>
  </div>
</td>

                  <td>{new Date(pedido.dataCriacao).toLocaleDateString("pt-BR")}</td>
<td>
  <div className="hg-actions-cell">
    <button
      className="hg-action-button hg-view"
      onClick={() => abrirModal(pedido)}
      title="Ver detalhes"
    >
      <Eye size={18} />
    </button>

    <button
      className="hg-action-button hg-print"
      onClick={() => gerarPDF(pedido)}
      title="Gerar PDF"
    >
      <Printer size={18} />
    </button>

    {pedido.statusPagamento?.toLowerCase() === "pendente" && (
      <button
        className="hg-action-button hg-pagar"
        onClick={() => irParaPagamento(pedido)}
        title="Pagar pedido"
      >
        <ShoppingBag size={18} />
      </button>
    )}
  </div>
</td>


                </motion.tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      )}

      <AnimatePresence>
        {pedidoSelecionado && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="hg-modal-overlay"
            onClick={fecharModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 20 }}
              className="hg-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="hg-close-button close" onClick={fecharModal}>
                <X size={20} />
              </button>

              <div className="hg-modal-header">
                <FileText size={24} className="hg-modal-icon" />
                <h2>Detalhes do Pedido</h2>
              </div>

              <div className="hg-modal-body">
                <div className="hg-detail-section">
                  <h3>Informações do Pedido</h3>
                  <div className="hg-detail-grid">
                    <div className="hg-detail-item">
                      <span className="hg-detail-label">Evento:</span>
                      <span className="hg-detail-value">{pedidoSelecionado.evento?.nome || "Não informado"}</span>
                    </div>
                    <div className="hg-detail-item">
                      <span className="hg-detail-label">Categoria:</span>
                      <span className="hg-detail-value">{pedidoSelecionado.categoria || "Não informado"}</span>
                    </div>
                    <div className="hg-detail-item">
                      <span className="hg-detail-label">Forma de Pagamento:</span>
                      <span className="hg-detail-value">{pedidoSelecionado.metodoPagamento || "Não informado"}</span>
                    </div>
                    <div className="hg-detail-item">
                      <span className="hg-detail-label">Valor Pago:</span>
                      <span className="hg-detail-value">
                        R$ {Number(pedidoSelecionado.total).toFixed(2).replace(".", ",")}
                      </span>

                    </div>
                    <div className="hg-detail-item">
                      <span className="hg-detail-label">Status do Pagamento:</span>
                      <span className={`hg-detail-value ${getStatusClass(pedidoSelecionado.statusPagamento)}`}>
                        {getStatusIcon(pedidoSelecionado.statusPagamento)}
                        {pedidoSelecionado.statusPagamento?.toUpperCase() || "DESCONHECIDO"}
                      </span>
                    </div>
                    <div className="hg-detail-item">
                      <span className="hg-detail-label">Data da Compra:</span>
                      <span className="hg-detail-value">
                        {new Date(pedidoSelecionado.dataCriacao).toLocaleDateString("pt-BR")}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="hg-detail-section">
                  <h3>Dados do Vaqueiro</h3>
                  <div className="hg-detail-grid">
                    <div className="hg-detail-item">
                      <span className="hg-detail-label">Nome do Vaqueiro:</span>
                      <span className="hg-detail-value">{pedidoSelecionado.nomeVaqueiro || "Não informado"}</span>
                    </div>
                    <div className="hg-detail-item">
                      <span className="hg-detail-label">CPF:</span>
                      <span className="hg-detail-value">{pedidoSelecionado.cpf || "Não informado"}</span>
                    </div>
                    <div className="hg-detail-item">
                      <span className="hg-detail-label">Nome do Esteira:</span>
                      <span className="hg-detail-value">{pedidoSelecionado.nomeEsteira || "Não informado"}</span>
                    </div>
                    <div className="hg-detail-item">
                      <span className="hg-detail-label">Cavalo de Puxada:</span>
                      <span className="hg-detail-value">{pedidoSelecionado.cavaloPuxar || "Não informado"}</span>
                    </div>
                    <div className="hg-detail-item">
                      <span className="hg-detail-label">Cavalo de Esteira:</span>
                      <span className="hg-detail-value">{pedidoSelecionado.cavaloEsteira || "Não informado"}</span>
                    </div>
                    <div className="hg-detail-item">
                      <span className="hg-detail-label">Representação:</span>
                      <span className="hg-detail-value">{pedidoSelecionado.representacao || "Não informado"}</span>
                    </div>
                    <div className="hg-detail-item">
                      <span className="hg-detail-label">Cidade:</span>
                      <span className="hg-detail-value">{pedidoSelecionado.cidade || "Não informado"}</span>
                    </div>
                  </div>
                </div>

                <div className="hg-detail-section">
                  <h3>Senhas Adquiridas</h3>
                  {pedidoSelecionado.senhas.length > 0 ? (
                    <div className="hg-senhas-grid">
                      {pedidoSelecionado.senhas.map((senha, index) => (
                        <div key={index} className={`hg-senha-card ${getStatusClass(senha.status)}`}>
                          <div className="hg-senha-number">{senha.numero}</div>
                          <div className="hg-senha-info">
                            <span className="hg-senha-label">Senha {index + 1}</span>
                            <span className="hg-senha-status">
                              {getStatusIcon(senha.status)}
                              {senha.status?.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="hg-no-data">Nenhuma senha encontrada.</p>
                  )}
                </div>
              </div>

              <div className="hg-modal-footer">
                {pedidoSelecionado.solicitacaoReembolso?.status ? (
                  <div className={`hg-refund-status ${pedidoSelecionado.solicitacaoReembolso.status === "concluido" ? "is-done" : ""}`}>
                    <RotateCcw size={17} />
                    <span>{nomeStatusReembolso(pedidoSelecionado.solicitacaoReembolso.status)}</span>
                    {pedidoSelecionado.solicitacaoReembolso.status === "falha_notificacao" && (
                      <button className="hg-refund-retry" onClick={() => setConfirmacaoReembolso(true)}>Tentar novamente</button>
                    )}
                  </div>
                ) : (pedidoSelecionado.statusPagamento?.toLowerCase() === "pago" ||
                    (pedidoSelecionado.metodoPagamento === "boleto_parcelado" && pedidoSelecionado.statusPagamento === "processando")) ? (
                  <button className="hg-refund-open" onClick={() => { setErroReembolso(""); setConfirmacaoReembolso(true) }}>
                    <RotateCcw size={17} /> Solicitar cancelamento e reembolso
                  </button>
                ) : null}
                <button className="hg-action-button hg-print" onClick={() => gerarPDF(pedidoSelecionado)}>
                  <Printer size={18} />
                  Gerar PDF
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {confirmacaoReembolso && pedidoSelecionado && (
          <motion.div
            className="hg-refund-overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => !enviandoReembolso && setConfirmacaoReembolso(false)}
          >
            <motion.div
              className="hg-refund-dialog"
              initial={{ opacity: 0, y: 14, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10 }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="hg-refund-icon"><ShieldCheck size={25} /></div>
              <button className="hg-refund-close" onClick={() => setConfirmacaoReembolso(false)} disabled={enviandoReembolso} aria-label="Fechar"><X size={20} /></button>
              <h3>Confirmar solicitação?</h3>
              <p>Ao confirmar, você solicita o cancelamento das senhas <strong>{pedidoSelecionado.senhas?.map((senha) => senha.numero).join(", ") || "deste pedido"}</strong>.</p>
              <div className="hg-refund-notice">
                {pagamentoEhPixAsaas(pedidoSelecionado)
                  ? "A solicitação será enviada à Asaas. O pedido só será cancelado e as senhas voltarão ao mapa depois da confirmação do estorno para a conta de origem."
                  : "A solicitação será enviada à equipe pelo WhatsApp. O reembolso será feito manualmente e as senhas só voltarão ao mapa depois que a equipe confirmar a conclusão."}
              </div>
              <p className="hg-refund-deadline">Disponível somente até um dia antes do encerramento das vendas do evento.</p>
              {erroReembolso && <div className="hg-refund-error" role="alert">{erroReembolso}</div>}
              <div className="hg-refund-actions">
                <button className="hg-refund-cancel" onClick={() => setConfirmacaoReembolso(false)} disabled={enviandoReembolso}>Voltar</button>
                <button className="hg-refund-confirm" onClick={enviarSolicitacaoReembolso} disabled={enviandoReembolso}>
                  {enviandoReembolso ? "Enviando..." : "Confirmar solicitação"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export default MeusPedidos
